import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, inboxApi, NOTIFICATION_DEFAULTS, type NotificationPreferences,
} from '@wa/shared';
import { useHeaderAction } from '@/settings/SettingsLayout';
import { permissionState, requestPermission } from '@/notifications/desktop';
import { useRealtime } from '@/notifications/RealtimeProvider';
import { playChime } from '@/notifications/chime';

/**
 * The agent's own notifications.
 *
 * <b>The only section of Settings that changes nothing anybody else can see</b>, and
 * therefore the only one with no permission on it. Every other section here edits the
 * account — a role, the opening hours, an API key — and is gated accordingly. This one
 * is a statement about which interruptions this person wants on their own screen, and
 * gating it would mean an agent needing an administrator to switch off a sound that
 * was annoying them.
 *
 * The screen is arranged in the order the questions actually get asked:
 *
 *   1. <b>Does the browser allow this at all?</b> First, because it is the one thing
 *      here that is not ours to set and the one thing that makes everything below it
 *      moot. An agent with eleven switches on and a denied permission sees nothing,
 *      and nothing on the server can tell them why.
 *   2. <b>How do you want to be told?</b> Popup, sound, badge.
 *   3. <b>What about?</b> Seven events, each its own switch.
 *   4. <b>Whose chats?</b> The one narrowing that is applied on the server.
 *
 * And a Send a test in the header, because four separate things have to be true
 * before a popup appears — the browser's permission, the tab not being focused on
 * that chat, the OS not being in a do-not-disturb mode, and the preference — and
 * every one of them fails silently. Without the button, an agent who has switched
 * everything on and hears nothing can only test it by asking a customer to write in.
 */
export function NotificationsPanel() {
  const queryClient = useQueryClient();
  const { refreshPermission, testsSeen, state: connection } = useRealtime();

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [draft, setDraft] = useState<NotificationPreferences>(NOTIFICATION_DEFAULTS);

  /*
   * What the test actually answers.
   *
   * "Sent" was a statement about an HTTP request and nothing more, which is close to
   * useless: four things have to work before a popup appears — the API, the websocket,
   * the browser's permission and the operating system — and all four fail the same
   * way, in silence.
   *
   * So the screen watches for the test notification arriving back over the socket. It
   * splits the silence in two, which is the whole diagnostic value: if it arrived and
   * nothing popped up, everything up to the browser worked and the problem is Windows
   * or Chrome's own notification setting. If it never arrived, the popup was never the
   * question.
   */
  const [test, setTest] = useState<{ at: number; seenAt: number } | null>(null);
  const arrived = test !== null && testsSeen > test.seenAt;
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (!test || arrived) return;
    // Three seconds. The round trip is a POST and a websocket frame; if it has not
    // landed by then it is not late, it is not coming.
    const timer = setTimeout(() => setGaveUp(true), 3000);
    return () => clearTimeout(timer);
  }, [test, arrived]);

  // Read locally rather than from the provider, because this screen changes it and
  // has to re-read after its own prompt.
  const [permission, setPermission] = useState(() => permissionState());

  const preferences = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: inboxApi.notificationPreferences,
  });

  useEffect(() => { if (preferences.data) setDraft(preferences.data); }, [preferences.data]);

  const save = useMutation({
    mutationFn: () => inboxApi.setNotificationPreferences(draft),
    onSuccess: (fresh) => {
      setError(null);
      setSaved(true);
      // The server's answer, not the draft: it is the authority on what was stored,
      // and a screen that shows what it sent rather than what came back is a screen
      // that lies the first time a field is dropped.
      queryClient.setQueryData(['notification-preferences'], fresh);
    },
    onError: (e) => {
      setSaved(false);
      setError(e instanceof ApiError ? e.message : 'Could not save your notification settings.');
    },
  });

  const send = useMutation({
    mutationFn: inboxApi.testNotification,
    onSuccess: () => {
      // The sound is played from here rather than waited for over the socket.
      // Pressing the button IS the gesture a browser requires before a page may
      // make noise, and it is the only moment in the app guaranteed to be one — so
      // the test is also what unlocks audio for the rest of the session. Waiting
      // for the round trip would spend that gesture and arrive without it.
      if (draft.sound) playChime();
    },
    onError: () => setError('Could not send the test — the API refused it.'),
  });

  const dirty = preferences.data !== undefined
    && KEYS.some((key) => draft[key] !== preferences.data![key]);

  const runTest = () => {
    setError(null);
    setGaveUp(false);
    // Snapshot the counter first: `arrived` is "more have come in since I pressed".
    setTest({ at: Date.now(), seenAt: testsSeen });
    send.mutate();
  };

  useHeaderAction(
    <button
      type="button"
      onClick={runTest}
      disabled={send.isPending}
      className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small font-medium text-(--color-ink) transition hover:bg-(--color-sunken) disabled:opacity-50"
    >
      Send a test
    </button>,
    // Deliberately not disabled when permission is missing. A test that refuses to
    // run until the popup is allowed cannot tell anybody whether the sound and the
    // connection work, which is most of what somebody debugging silence needs.
    [send.isPending],
  );

  const set = (patch: Partial<NotificationPreferences>) => {
    setSaved(false);
    setTest(null);
    setGaveUp(false);
    setDraft((d) => ({ ...d, ...patch }));
  };

  const enable = async () => {
    const next = await requestPermission();
    setPermission(next);
    refreshPermission();
  };

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}
      {saved && !save.isPending && (
        <p role="status" className="rounded-(--radius-field) border border-(--color-good) px-3 py-2 text-small text-(--color-good)">
          Saved. This applies to every tab and device you are signed in on.
        </p>
      )}
      {test && <TestResult arrived={arrived} gaveUp={gaveUp} permission={permission} connection={connection} />}

      <BrowserPermission state={permission} onEnable={() => void enable()} />

      <Group
        title="How you are told"
        blurb="Three separate switches because they annoy different people. An open-plan office turns the sound off and keeps the popup."
      >
        <Row
          label="Desktop popup"
          hint="A notification from your browser, even when this tab is in the background."
          on={draft.desktop}
          onChange={(desktop) => set({ desktop })}
        />
        <Row
          label="Sound"
          hint="A short chime. Your browser will not let it play until you have clicked somewhere in the app."
          on={draft.sound}
          onChange={(sound) => set({ sound })}
        />
        <Row
          label="Count on the tab"
          hint="The number of chats with something unread, on the tab title and icon."
          on={draft.badge}
          onChange={(badge) => set({ badge })}
        />
      </Group>

      <Group
        title="What you are told about"
        blurb="Switched off here means the event never leaves the server for your browser — not that it arrives and is hidden."
      >
        <Row
          label="A customer writes in"
          hint="The loud one. Narrow it with the setting below rather than switching it off."
          on={draft.newMessage}
          onChange={(newMessage) => set({ newMessage })}
        />
        <Row
          label="A chat is assigned to you"
          hint="By a rule, by a bot handing over, or by a colleague."
          on={draft.assigned}
          onChange={(assigned) => set({ assigned })}
        />
        <Row
          label="Somebody mentions you in a note"
          hint="A colleague typed your name into an internal note, which means they are waiting on you."
          on={draft.mention}
          onChange={(mention) => set({ mention })}
        />
        <Row
          label="A response target is about to be missed"
          hint="A quarter of the target before the deadline, so the warning is proportionate at fifteen minutes and at four hours."
          on={draft.slaApproaching}
          onChange={(slaApproaching) => set({ slaApproaching })}
          extra={<TargetsNote />}
        />
        <Row
          label="A response target was missed"
          hint="Once, for the deadline that passed. Not again every minute afterwards."
          on={draft.slaBreached}
          onChange={(slaBreached) => set({ slaBreached })}
        />
        <Row
          label="A broadcast you launched has finished"
          hint="With how many were sent and how many failed, so the number that matters is not behind a click."
          on={draft.campaignFinished}
          onChange={(campaignFinished) => set({ campaignFinished })}
        />
        <Row
          label="Meta answers on a template"
          hint="Approved, rejected, paused or disabled. Only if you may manage templates."
          on={draft.templateStatus}
          onChange={(templateStatus) => set({ templateStatus })}
        />
      </Group>

      <Group
        title="Whose chats"
        blurb="Applied on the server, so what you switch off here is never sent to you at all."
      >
        <Row
          label="Only chats assigned to me"
          hint="On, you hear nothing about the unassigned queue. Useful on a large team where the queue is somebody else's job; on a team of two it means missing the chats nobody has picked up."
          on={draft.onlyMyChats}
          onChange={(onlyMyChats) => set({ onlyMyChats })}
        />
      </Group>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => { setError(null); save.mutate(); }}
          disabled={!dirty || save.isPending}
          className="tap rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) transition hover:opacity-90 disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {dirty && <span className="text-small text-(--color-muted)">Unsaved changes.</span>}
      </div>
    </div>
  );
}

/**
 * What the test proved, and what it did not.
 *
 * Three states, and the middle one is the point: <b>the event reached this tab.</b>
 * That single fact rules out the API, the websocket, the agent group and every
 * preference in one go, and leaves exactly two candidates — the browser's permission,
 * or the operating system. Before this, all of those failed identically and the only
 * information anybody had was "nothing happened".
 */
function TestResult({ arrived, gaveUp, permission, connection }: {
  arrived: boolean;
  gaveUp: boolean;
  permission: ReturnType<typeof permissionState>;
  connection: string;
}) {
  if (arrived) {
    return (
      <div className="rounded-(--radius-field) border border-(--color-good) bg-(--color-surface) px-3 py-2.5 text-small">
        <p className="font-semibold text-(--color-ink)">The test reached this tab.</p>
        <p className="mt-1 text-(--color-muted)">
          {permission === 'granted'
            ? 'So the connection and your settings are fine. If no popup appeared, it was stopped after that — check that notifications are allowed for Chrome in Windows Settings → System → Notifications, and that Focus assist is off.'
            : 'The connection is fine. No popup appeared because your browser has not allowed them — see below.'}
        </p>
      </div>
    );
  }

  if (!gaveUp) {
    return (
      <p role="status" className="rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small text-(--color-muted)">
        Sent. Waiting for it to come back…
      </p>
    );
  }

  return (
    <div className="rounded-(--radius-field) border border-(--color-warn) bg-(--color-warn-wash) px-3 py-2.5 text-small">
      <p className="font-semibold text-(--color-ink)">The test never came back to this tab.</p>
      <p className="mt-1 text-(--color-muted)">
        {connection === 'connected'
          ? 'The live connection is up, so the API accepted the test and then did not deliver it. That is a server-side problem, not a browser one — the most likely cause is an API that has not been restarted since notifications were added.'
          : `The live connection is ${connection}. Nothing can reach this tab until it is back, and a popup is not the thing to fix first.`}
      </p>
    </div>
  );
}

/**
 * The browser's own answer, which is not ours to change and is the most common reason
 * this whole screen appears to do nothing.
 *
 * Three states worth three different things to say. `denied` is the one that matters:
 * it cannot be undone from a page — only from the browser's site settings — so
 * offering a button would be offering something that does nothing, and the honest
 * answer is to say where the setting is.
 */
function BrowserPermission({ state, onEnable }: {
  state: ReturnType<typeof permissionState>; onEnable: () => void;
}) {
  if (state === 'granted') {
    return (
      <p className="flex items-center gap-2 rounded-lg border border-(--color-good) bg-(--color-surface) px-3 py-2.5 text-small text-(--color-ink) sm:px-4">
        <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4.5 shrink-0 text-(--color-good)">
          <path d="m5.5 10.5 3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Your browser allows notifications from this site.
      </p>
    );
  }

  if (state === 'unsupported') {
    return (
      <p className="rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-2.5 text-small text-(--color-muted) sm:px-4">
        This browser cannot show desktop notifications. The sound and the count on the
        tab still work.
      </p>
    );
  }

  if (state === 'denied') {
    return (
      <div className="rounded-lg border border-(--color-warn) bg-(--color-warn-wash) px-3 py-2.5 sm:px-4">
        <p className="text-small font-semibold text-(--color-ink)">
          Your browser is blocking notifications from this site.
        </p>
        <p className="mt-1 text-small text-(--color-muted)">
          A page cannot undo this — only you can, from your browser. Click the padlock
          or the icon at the left of the address bar, find Notifications, and set it to
          Allow. Everything below keeps working; only the popup is blocked.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-(--color-brand) bg-(--color-brand-wash) px-3 py-2.5 sm:px-4">
      <p className="min-w-0 flex-1 text-small text-(--color-ink)">
        <span className="font-semibold">Your browser has not been asked yet.</span>{' '}
        Until it is, nothing below can show a popup. It has to be a button, because a
        browser only accepts the request from a click.
      </p>
      <button
        type="button"
        onClick={onEnable}
        className="tap shrink-0 rounded-(--radius-field) bg-(--color-brand) px-3 py-1.5 text-small font-semibold text-(--color-brand-ink) transition hover:opacity-90"
      >
        Ask now
      </button>
    </div>
  );
}

function TargetsNote() {
  return (
    <p className="mt-1 text-small text-(--color-muted)">
      Nothing arrives unless somebody has set{' '}
      <Link to="/settings/targets" className="underline">response targets</Link> for
      this account.
    </p>
  );
}

function Group({ title, blurb, children }: {
  title: string; blurb: string; children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
      <header className="border-b border-(--color-line-dim) px-3 py-3 sm:px-4">
        <h2 className="text-lead font-semibold tracking-tight">{title}</h2>
        <p className="mt-0.5 text-small text-(--color-muted)">{blurb}</p>
      </header>
      <div className="divide-y divide-(--color-line-dim)">{children}</div>
    </section>
  );
}

function Row({ label, hint, on, onChange, extra }: {
  label: string; hint: string; on: boolean;
  onChange: (on: boolean) => void; extra?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 px-3 py-3 sm:px-4">
      <div className="min-w-0 flex-1">
        <p className="text-body font-medium text-(--color-ink)">{label}</p>
        <p className="mt-0.5 text-small text-(--color-muted)">{hint}</p>
        {extra}
      </div>
      <Switch on={on} onChange={onChange} label={label} />
    </div>
  );
}

function Switch({ on, onChange, label }: { on: boolean; onChange: (on: boolean) => void; label: string }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} aria-label={label}
      onClick={() => onChange(!on)}
      className={`tap relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${on ? 'bg-(--color-brand)' : 'bg-(--color-line)'}`}
    >
      <span className={`inline-block size-5 rounded-full bg-white shadow transition ${on ? 'translate-x-5.5' : 'translate-x-0.5'}`} />
    </button>
  );
}

/**
 * Every field, listed once, so `dirty` cannot silently stop noticing one.
 *
 * Typed as the keys of the interface: adding a preference without adding it here is
 * a compile error, not a Save button that never enables.
 */
const KEYS = Object.keys(NOTIFICATION_DEFAULTS) as (keyof NotificationPreferences)[];
